import {ContentCopy} from '@mui/icons-material';
import {Box, CircularProgress, IconButton, Tooltip} from '@mui/material';
import {useCallback, useRef, useState} from 'react';
import TextWithRenderer from '../../atoms/textWithRenderer/TextWithRenderer';
import BondGraphRenderer from '../bondGraphRenderer/BondGraphRenderer';
import {useCopyGraphAsImage} from '../bondGraphRenderer/hooks/useCopyGraphAsImage';

export interface BondGraphJsonRendererProps {
  graphJson: string;
  onGraphJsonChange: (value: string) => void;
}

/**
 * provide textfield to input graph json and render graph
 * @param graphJson - The graph JSON string to parse
 * @param onGraphJsonChange - The function to call when the graph JSON string changes
 * @returns
 */
export default function BondGraphJsonRenderer({graphJson, onGraphJsonChange}: BondGraphJsonRendererProps) {
  const graphRef = useRef<HTMLDivElement>(null);
  const {copyGraphAsImage, copying: copyingGraph} = useCopyGraphAsImage(graphRef);
  const [graphJsonError, setGraphJsonError] = useState<string>('');

  const hasGraphJson = Boolean(graphJson?.trim());
  const canCopyGraph = hasGraphJson && !graphJsonError;

  const handleCopyGraphAsPng = useCallback(async () => {
    if (!canCopyGraph) return;
    await copyGraphAsImage('png');
  }, [canCopyGraph, copyGraphAsImage]);

  return (
    <Box data-testid="bond-graph-json-renderer">
      <TextWithRenderer
        fullWidth
        label="Graph JSON (Optional)"
        multiline
        rows={4}
        value={graphJson}
        onChange={useCallback(
          (e: React.ChangeEvent<HTMLTextAreaElement>) => onGraphJsonChange(e.target.value),
          [onGraphJsonChange],
        )}
        helperText={
          graphJsonError ||
          'Enter JSON with graphType, x: {label, min?, max?}, y1/y2: {label, min?, max?, data: {title1: [{x, y}], title2: [{x, y}]}}'
        }
        error={!!graphJsonError}
        placeholder='{"graphType": "line", "x": {"label": "Time"}, "y1": {"label": "Value", "data": {"Series 1": [{"x": 1, "y": 2}], "Series 2": [{"x": 1, "y": 3}]}}}'
      />

      {hasGraphJson && (
        <Box sx={{position: 'relative'}}>
          <BondGraphRenderer
            ref={graphRef}
            graphJsonString={graphJson}
            onErrorMessage={(message) => setGraphJsonError(message)}
          />
          {canCopyGraph && (
            <Tooltip title="Copy graph as PNG">
              <span>
                <IconButton
                  onClick={handleCopyGraphAsPng}
                  disabled={copyingGraph}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': {bgcolor: 'background.paper', boxShadow: 3},
                  }}
                  size="small"
                >
                  {copyingGraph ? <CircularProgress size={20} /> : <ContentCopy fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}
